import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { CANADIAN_PROVINCES } from '../../data/canadianProvinces';
import AuthLayout, { AUTH_INPUT_CLASS, AUTH_BUTTON_CLASS } from './AuthLayout';

// Select needs slightly different styling (native dropdown options)
const SELECT_CLASS = AUTH_INPUT_CLASS + ' appearance-none cursor-pointer';

export default function CompleteProfile() {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        phone: '',
        address: '',
        address2: '',
        postalCode: '',
        city: '',
        province: '',
        country: 'Canada',
        flightSchool: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newFormData = { ...formData, [name]: value };
        if (name === 'province') {
            newFormData.country = value === 'XX' ? '' : 'Canada';
        }
        setFormData(newFormData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/user/complete-profile.php`,
                formData,
                { withCredentials: true }
            );
            if (response.data.success) {
                setUser({ ...user, ...response.data.user, profileCompleted: true });
                navigate('/dashboard');
            } else {
                setError('Erreur lors de la mise à jour du profil');
            }
        } catch (err) {
            console.error('Profile completion error:', err);
            setError(err.response?.data?.error || 'Une erreur est survenue');
        } finally {
            setLoading(false);
        }
    };

    const labelClass = "block text-xs font-semibold text-gray-700 dark:text-white/70 mb-2 uppercase tracking-wider";
    const required = <span className="text-red-600 dark:text-red-400">*</span>;

    return (
        <AuthLayout
            title="Complétez votre profil"
            subtitle="Ces informations sont nécessaires pour accéder aux examens et gérer vos commandes"
            maxWidth="max-w-2xl"
        >
            {error && (
                <div className="mb-5 p-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Prénom & Nom */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>Prénom {required}</label>
                        <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={AUTH_INPUT_CLASS} />
                    </div>
                    <div>
                        <label className={labelClass}>Nom {required}</label>
                        <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={AUTH_INPUT_CLASS} />
                    </div>
                </div>

                <div>
                    <label className={labelClass}>Téléphone {required}</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={AUTH_INPUT_CLASS} placeholder="(XXX) XXX-XXXX" />
                </div>

                <div>
                    <label className={labelClass}>Adresse {required}</label>
                    <input type="text" name="address" value={formData.address} onChange={handleChange} required className={AUTH_INPUT_CLASS} />
                </div>

                <div>
                    <label className={labelClass}>Adresse (suite)</label>
                    <input type="text" name="address2" value={formData.address2} onChange={handleChange} className={AUTH_INPUT_CLASS} placeholder="Appartement, bureau, etc." />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>Code postal {required}</label>
                        <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} required className={AUTH_INPUT_CLASS} placeholder="A1B 2C3" />
                    </div>
                    <div>
                        <label className={labelClass}>Ville {required}</label>
                        <input type="text" name="city" value={formData.city} onChange={handleChange} required className={AUTH_INPUT_CLASS} />
                    </div>
                </div>

                <div>
                    <label className={labelClass}>Province/Territoire {required}</label>
                    <select name="province" value={formData.province} onChange={handleChange} required className={SELECT_CLASS}>
                        <option value="" className="text-gray-900 bg-white">Sélectionnez une province</option>
                        {CANADIAN_PROVINCES.map(p => (
                            <option key={p.code} value={p.code} className="text-gray-900 bg-white">{p.fr}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className={labelClass}>Pays {required}</label>
                    <input
                        type="text" name="country" value={formData.country} onChange={handleChange} required
                        readOnly={formData.province !== 'XX'}
                        className={`${AUTH_INPUT_CLASS} ${formData.province !== 'XX' ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                </div>

                <div>
                    <label className={labelClass}>École de Pilotage (optionnel)</label>
                    <input type="text" name="flightSchool" value={formData.flightSchool} onChange={handleChange} className={AUTH_INPUT_CLASS} placeholder="Nom de votre école" />
                </div>

                <button type="submit" disabled={loading} className={`${AUTH_BUTTON_CLASS} mt-3`}>
                    {loading ? 'Enregistrement...' : 'Valider mon profil'}
                </button>
            </form>
        </AuthLayout>
    );
}
